import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import toast from './utils/toast'
import PWAUpdateManager from './components/base/PWAUpdateManager'
import * as Sentry from '@sentry/react'

// Sentry (init condicional)
try {
  const env = (import.meta as any).env || {}
  const dsn = env.VITE_SENTRY_DSN as string | undefined
  if (dsn) {
    const traces = Number(env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 0.2)
    const onError = Number(env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0)
    const session = Number(env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.0)

    Sentry.init({
      dsn,
      environment: env.VITE_SENTRY_ENVIRONMENT || 'production',
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })
      ],
      tracesSampleRate: traces,
      replaysOnErrorSampleRate: onError,
      replaysSessionSampleRate: session,
      beforeSend(event: any) {
        // Remover PII (LGPD)
        if (event?.user) delete event.user
        if (event?.request) {
          if (event.request.headers) delete event.request.headers
          if (event.request.cookies) delete event.request.cookies
          if (event.request.data) delete event.request.data
        }
        return event
      }
    })
    if (env.DEV) {
      console.info('[Sentry] Initialized')
    }
  } else {
    // Silencioso em produção - não precisa avisar que Sentry não está configurado
    if (env.DEV) {
      console.info('[Sentry] DSN não configurado; monitoramento inativo')
    }
  }
} catch (error) {
  console.warn('[Sentry] Falhou ao inicializar:', error)
}

// Polyfill para redirecionar alert() → toast.info
try {
  const w = window as any
  if (!w.__toastAlertPolyfillApplied) {
    w.__toastAlertPolyfillApplied = true
    const originalAlert = window.alert.bind(window)
    window.alert = (message?: any) => {
      try {
        const text = String(message ?? '')
        const lower = text.toLowerCase()
        if (
          lower.includes('erro') ||
          lower.includes('falha') ||
          lower.includes('invalido') ||
          lower.includes('inválido')
        ) {
          toast.error(text)
        } else if (
          lower.includes('sucesso') ||
          lower.includes('conclu') || /* concluído/concluido */
          lower.includes('adicion') || /* adicionado */
          lower.includes('removid') || /* removido */
          lower.includes('exclu') || /* excluído/excluido */
          lower.includes('salv') || /* salvo/salvar */
          lower.includes('atualiz')
        ) {
          toast.success(text)
        } else if (
          lower.includes('por favor') ||
          lower.includes('informe') ||
          lower.includes('atenção') ||
          lower.includes('confirmar')
        ) {
          toast.warning(text)
        } else {
          toast.info(text)
        }
      } catch {
        originalAlert(String(message))
      }
    }
  }
} catch {
  // ignora em ambientes sem window
}

// Registrar Service Worker do PWA (auto update)
// Gerenciado por PWAUpdateManager

const root = createRoot(document.getElementById('root')!)

// StrictMode causa double-render em dev, causando lentidão desnecessária
// Mantenha em dev apenas para debug, mas desabilite em produção
const ENABLE_STRICT_MODE = false // Desabilitado temporariamente

if (import.meta.env.DEV && ENABLE_STRICT_MODE) {
  root.render(
    <StrictMode>
      <PWAUpdateManager />
      <App />
    </StrictMode>
  )
} else {
  root.render(
    <>
      <PWAUpdateManager />
      <App />
    </>
  )
}
